// Exposure, data-broker, and company fixtures for demo mode.
//
// Every exposure, company, suppression, and conversation the exposures/companies/public
// services can ask for is generated here from one seed list so the account
// reads as one coherent privacy footprint — not forty-five unrelated rows.

import type { DemoRoute } from '../types';
import { envelope, refusal } from '../envelope';
import { DEMO_PERSON_ID, DEMO_PERSONA, demoDaysAgo } from '../persona';
import { COMPANY_CATALOG } from './company-catalog';

import type { BaseAPIResponse, Meta } from '~/types/api';
import { ConversationMessageType } from '~/types/person/conversations';
import type { CompanyAdvisory, Company as CompanyDetail, DeletionGuideResponse } from '~/types/person/companies';
import { ScannerStatus } from '~/types/person/companies';
import {
    ExposureNextAction,
    ExposureRemediation,
    ExposureSeverity,
    ExposureStatus,
    getCompanyTypeLabel,
    type CompanyExposure,
    type CompanyType,
    type CoverageStatus,
    type Exposure,
    type PersonExposure,
    type RemovalActivity,
} from '~/types/person/details';
import {
    DEFAULT_EXPOSURE_STATS_WINDOW,
    EXPOSURE_STATS_WINDOWS,
    type DigitalFootprintFigures,
    type ExposureStats,
    type ExposureStatsWindow,
} from '~/types/person/exposure-stats';
import type { ScanAddress, ScanRecord, ScanRelative } from '~/types/person/scan';
import type { Company as VaultCompanyRef, PotentialExposuresResponseData, SuppressionRecord } from '~/types/person/vault';
import type { CompanyExposureDetail, RecentExposure } from '~/services/exposures-service';
import type { ConversationMessage } from '~/types/companies/conversations';

// ---------------------------------------------------------------------------
// Company catalog
// ---------------------------------------------------------------------------

interface BrokerSeed {
    id: number;
    name: string;
    slug: string;
    domain: string;
    type: CompanyType;
    /** Logo file extension on the Privacy Bee CDN — it is not uniform across the catalog. */
    logoExt: 'png' | 'jpg';
}

// Real Privacy Bee company records, so the demo shows the same identities, slugs and logo
// artwork a live account would. Ids, slugs, types and logo extensions come from the public
// company catalog; only the exposures attached to them are fabricated.
//
// The 18 brokers PB is actively monitoring for Michael. Order matches EXPOSURE_SEEDS below.
const BROKERS: BrokerSeed[] = [
    { id: 1396, name: 'Spokeo', slug: 'spokeo', domain: 'spokeo.com', type: 'people_search', logoExt: 'png' },
    { id: 6077, name: 'Whitepages', slug: 'whitepages', domain: 'whitepages.com', type: 'people_search', logoExt: 'png' },
    { id: 2241, name: 'BeenVerified', slug: 'beenverified', domain: 'beenverified.com', type: 'people_search', logoExt: 'jpg' },
    { id: 3597, name: 'Intelius', slug: 'intelius', domain: 'intelius.com', type: 'people_search', logoExt: 'png' },
    { id: 23712, name: 'Radaris', slug: 'radaris', domain: 'radaris.com', type: 'people_search', logoExt: 'png' },
    { id: 4197, name: 'MyLife', slug: 'mylife', domain: 'mylife.com', type: 'people_search', logoExt: 'png' },
    { id: 23696, name: 'PeopleFinders', slug: 'peoplefinders', domain: 'peoplefinders.com', type: 'people_search', logoExt: 'png' },
    { id: 203, name: 'TruePeopleSearch', slug: 'truepeoplesearch', domain: 'truepeoplesearch.com', type: 'people_search', logoExt: 'png' },
    { id: 31054, name: 'FastPeopleSearch.com', slug: 'fastpeoplesearch-com', domain: 'fastpeoplesearch.com', type: 'people_search', logoExt: 'png' },
    { id: 186, name: 'Instant Checkmate', slug: 'instant-checkmate', domain: 'instantcheckmate.com', type: 'people_search', logoExt: 'jpg' },
    { id: 1205, name: 'US Search', slug: 'us-search', domain: 'ussearch.com', type: 'people_search', logoExt: 'png' },
    { id: 2483, name: 'CheckPeople', slug: 'checkpeople', domain: 'checkpeople.com', type: 'people_search', logoExt: 'png' },
    { id: 1203, name: 'PublicRecordsNOW', slug: 'publicrecordsnow', domain: 'publicrecordsnow.com', type: 'people_search', logoExt: 'png' },
    { id: 7, name: 'Acxiom', slug: 'acxiom', domain: 'acxiom.com', type: 'data_broker', logoExt: 'png' },
    { id: 42510, name: 'Epsilon', slug: 'epsilon', domain: 'epsilon.com', type: 'data_broker', logoExt: 'png' },
    { id: 189, name: 'LexisNexis', slug: 'lexisnexis', domain: 'lexisnexis.com', type: 'data_enrichment', logoExt: 'png' },
    { id: 26488, name: 'ZoomInfo', slug: 'zoominfo', domain: 'zoominfo.com', type: 'data_enrichment', logoExt: 'png' },
    { id: 31325, name: 'SearchPeopleFree.com', slug: 'searchpeoplefree-com', domain: 'searchpeoplefree.com', type: 'people_search', logoExt: 'png' },
];

// Every company Privacy Bee monitors, from the real public catalog. The eighteen curated
// brokers above carry Michael's detailed exposure history; the rest carry the monitoring
// record that makes the account's coverage numbers the product's true scale.
const CATALOG: BrokerSeed[] = COMPANY_CATALOG.map(([id, name, slug, type, domain, logoExt]) => ({
    id,
    name,
    slug,
    domain,
    type,
    logoExt,
}));

const CURATED_IDS = new Set(BROKERS.map((b) => b.id));

/** The monitored set: curated brokers first, then the rest of the catalog. */
const MONITORED: BrokerSeed[] = [...BROKERS, ...CATALOG.filter((c) => !CURATED_IDS.has(c.id))];

const ALL_COMPANIES = MONITORED;

const logoFor = (broker: BrokerSeed) => `https://cdn.privacybee.com/company-logo/production/${broker.id}.${broker.logoExt}`;
const urlFor = (domain: string) => `https://www.${domain}`;
const profileUrlFor = (broker: BrokerSeed) => `${urlFor(broker.domain)}/profile/michael-scott-${DEMO_PERSON_ID}`;
const typeLabelOf = (broker: BrokerSeed) => ({ value: broker.type, label: getCompanyTypeLabel(broker.type) });

/** The small `{company_id, name, last_scan, url, logo, slug}` shape the vault attaches per-exposure. */
function vaultCompanyRef(broker: BrokerSeed, lastScanDaysAgo: number): VaultCompanyRef {
    return {
        company_id: broker.id,
        name: broker.name,
        last_scan: demoDaysAgo(lastScanDaysAgo),
        url: urlFor(broker.domain),
        logo: logoFor(broker),
        slug: broker.slug,
    };
}

function companyMini(broker: BrokerSeed) {
    return { id: broker.id, name: broker.name, slug: broker.slug, type: broker.type, logo: logoFor(broker) };
}

function findBroker(companyId: number): BrokerSeed {
    return ALL_COMPANIES.find((c) => c.id === companyId) ?? {
        id: companyId,
        name: `Company #${companyId}`,
        slug: `company-${companyId}`,
        domain: 'example.com',
        type: 'standard',
        logoExt: 'png',
    };
}

// ---------------------------------------------------------------------------
// Persona data — the facts every exposure draws its "detected data" from
// ---------------------------------------------------------------------------

const PERSONA_ADDRESSES: ScanAddress[] = [
    {
        line1: DEMO_PERSONA.address.line_1,
        line2: DEMO_PERSONA.address.line_2,
        city: DEMO_PERSONA.address.city,
        state: DEMO_PERSONA.address.state,
        postal_code: DEMO_PERSONA.address.postal_code,
        country: DEMO_PERSONA.address.country,
    },
    { line1: '305 Pine Street', line2: 'Apt 4B', city: 'Scranton', state: 'PA', postal_code: '18503', country: 'US' },
    { line1: '88 Lakewood Drive', line2: '', city: 'Scranton', state: 'PA', postal_code: '18509', country: 'US' },
];

const PERSONA_EMAILS = [DEMO_PERSONA.email, 'michael.scott@dundermifflin.com', 'mscott1964@gmail.com'];
const PERSONA_PHONES = [DEMO_PERSONA.phone, '+15705559981'];
const PERSONA_RELATIVES: ScanRelative[] = [
    { first_name: 'Deborah', last_name: 'Scott', name: 'Deborah Scott', age: '78' },
    { first_name: 'Nate', last_name: 'Scott', name: 'Nate Scott', age: '52' },
];
const PERSONA_AKA = ['Michael G Scott', 'Mike Scott'];

function personaAge(): number {
    const dob = new Date(DEMO_PERSONA.dob);
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    const monthDelta = now.getMonth() - dob.getMonth();
    if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < dob.getDate())) age--;
    return age;
}

// A screenshot of an exposure is the whole point of the evidence thumbnail: it has to show
// the listing as the broker publishes it, with the person's own data on it. Drawn as an inline
// SVG rather than fetched, so the demo shows real exposed values without a stock photo standing
// in for them, and without any network dependency.
function profileScreenshot(broker: BrokerSeed, addressIdx: number): string {
    const address = PERSONA_ADDRESSES[addressIdx % PERSONA_ADDRESSES.length];
    const escape = (value: string) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const rows: Array<[string, string]> = [
        ['Age', `${personaAge()} years old`],
        ['Lives in', `${address.city}, ${address.state} ${address.postal_code}`],
        ['Last address', `${address.line1}${address.line2 ? ` ${address.line2}` : ''}`],
        ['Phone', PERSONA_PHONES[addressIdx % PERSONA_PHONES.length]],
        ['Email', PERSONA_EMAILS[addressIdx % PERSONA_EMAILS.length]],
        ['Also known as', PERSONA_AKA.join(', ')],
        ['Relatives', PERSONA_RELATIVES.map((r) => r.name).join(', ')],
    ];

    const rowMarkup = rows
        .map(([label, value], i) => {
            const y = 150 + i * 32;
            return (
                `<text x="40" y="${y}" font-family="Helvetica, Arial, sans-serif" font-size="13" fill="#8a94a6">${escape(label)}</text>` +
                `<text x="190" y="${y}" font-family="Helvetica, Arial, sans-serif" font-size="14" fill="#12161f">${escape(value)}</text>` +
                `<line x1="40" y1="${y + 12}" x2="600" y2="${y + 12}" stroke="#eef1f6" stroke-width="1"/>`
            );
        })
        .join('');

    const svg =
        `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="400" viewBox="0 0 640 400">` +
        `<rect width="640" height="400" fill="#ffffff"/>` +
        `<rect width="640" height="46" fill="#1f2a44"/>` +
        `<text x="24" y="29" font-family="Helvetica, Arial, sans-serif" font-size="15" font-weight="bold" fill="#ffffff">${escape(broker.name)}</text>` +
        `<text x="24" y="72" font-family="Helvetica, Arial, sans-serif" font-size="11" fill="#8a94a6">${escape(`www.${broker.domain}/profile/michael-scott`)}</text>` +
        `<circle cx="62" cy="108" r="22" fill="#e6eaf2"/>` +
        `<text x="62" y="114" text-anchor="middle" font-family="Helvetica, Arial, sans-serif" font-size="16" font-weight="bold" fill="#5b6b8a">MS</text>` +
        `<text x="96" y="106" font-family="Helvetica, Arial, sans-serif" font-size="20" font-weight="bold" fill="#12161f">${escape(DEMO_PERSONA.full_name)}</text>` +
        `<text x="96" y="126" font-family="Helvetica, Arial, sans-serif" font-size="12" fill="#8a94a6">Public record &#183; ${escape(`${address.city}, ${address.state}`)}</text>` +
        rowMarkup +
        `<rect x="40" y="368" width="150" height="26" rx="4" fill="#d64545"/>` +
        `<text x="115" y="385" text-anchor="middle" font-family="Helvetica, Arial, sans-serif" font-size="12" font-weight="bold" fill="#ffffff">View Full Report</text>` +
        `</svg>`;

    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function scanRecordFor(broker: BrokerSeed, addressIdx: number): ScanRecord {
    return {
        first_name: DEMO_PERSONA.first_name,
        last_name: DEMO_PERSONA.last_name,
        age: String(personaAge()),
        date_of_birth: DEMO_PERSONA.dob,
        aka: PERSONA_AKA,
        addresses: [PERSONA_ADDRESSES[addressIdx % PERSONA_ADDRESSES.length]],
        emails: [DEMO_PERSONA.email],
        phones: [DEMO_PERSONA.phone],
        relatives: PERSONA_RELATIVES,
        screenshot: profileScreenshot(broker, addressIdx),
        url: profileUrlFor(broker),
    };
}

type DetectedCategory = 'name' | 'date_of_birth' | 'age' | 'addresses' | 'phones' | 'emails' | 'relatives' | 'employment';

function detectedCategoriesFor(type: CompanyType): DetectedCategory[] {
    if (type === 'people_search' || type === 'people_search_affiliate') {
        return ['name', 'date_of_birth', 'age', 'addresses', 'phones', 'emails', 'relatives'];
    }
    if (type === 'background_check') {
        return ['name', 'date_of_birth', 'addresses', 'employment', 'relatives'];
    }
    if (type === 'data_broker' || type === 'data_enrichment') {
        return ['name', 'addresses', 'phones', 'emails', 'employment'];
    }
    return ['name', 'emails', 'phones']; // advertising_marketing and anything else
}

// ---------------------------------------------------------------------------
// The 45 exposures — one compact seed row each, expanded into every response shape below
// ---------------------------------------------------------------------------

const { EXPOSED, PENDING, IN_PROGRESS, CLOSED, FAILED, NOT_FOUND } = ExposureStatus;
const { CRITICAL, HIGH, MEDIUM, LOW } = ExposureSeverity;
const { MANUAL, GRT, SST, RRT, EMAIL } = ExposureRemediation;

interface ExposureSeed {
    id: string;
    companyIdx: number;
    status: ExposureStatus;
    severity: ExposureSeverity;
    remediation: ExposureRemediation;
    createdDaysAgo: number;
}

const EXPOSURE_SEEDS: ExposureSeed[] = [
    { id: 'exp-1', companyIdx: 0, status: EXPOSED, severity: CRITICAL, remediation: MANUAL, createdDaysAgo: 4 },
    { id: 'exp-2', companyIdx: 0, status: PENDING, severity: HIGH, remediation: GRT, createdDaysAgo: 12 },
    { id: 'exp-3', companyIdx: 0, status: CLOSED, severity: MEDIUM, remediation: SST, createdDaysAgo: 95 },
    { id: 'exp-4', companyIdx: 1, status: EXPOSED, severity: HIGH, remediation: MANUAL, createdDaysAgo: 6 },
    { id: 'exp-5', companyIdx: 1, status: IN_PROGRESS, severity: MEDIUM, remediation: GRT, createdDaysAgo: 30 },
    { id: 'exp-6', companyIdx: 1, status: CLOSED, severity: LOW, remediation: RRT, createdDaysAgo: 110 },
    { id: 'exp-7', companyIdx: 2, status: PENDING, severity: HIGH, remediation: MANUAL, createdDaysAgo: 9 },
    { id: 'exp-8', companyIdx: 2, status: CLOSED, severity: MEDIUM, remediation: SST, createdDaysAgo: 80 },
    { id: 'exp-9', companyIdx: 3, status: EXPOSED, severity: CRITICAL, remediation: MANUAL, createdDaysAgo: 3 },
    { id: 'exp-10', companyIdx: 3, status: IN_PROGRESS, severity: HIGH, remediation: GRT, createdDaysAgo: 25 },
    { id: 'exp-11', companyIdx: 4, status: PENDING, severity: MEDIUM, remediation: EMAIL, createdDaysAgo: 14 },
    { id: 'exp-12', companyIdx: 4, status: CLOSED, severity: LOW, remediation: SST, createdDaysAgo: 130 },
    { id: 'exp-13', companyIdx: 5, status: EXPOSED, severity: HIGH, remediation: MANUAL, createdDaysAgo: 5 },
    { id: 'exp-14', companyIdx: 5, status: CLOSED, severity: MEDIUM, remediation: RRT, createdDaysAgo: 100 },
    { id: 'exp-15', companyIdx: 6, status: PENDING, severity: HIGH, remediation: GRT, createdDaysAgo: 11 },
    { id: 'exp-16', companyIdx: 6, status: IN_PROGRESS, severity: MEDIUM, remediation: SST, createdDaysAgo: 40 },
    { id: 'exp-17', companyIdx: 7, status: EXPOSED, severity: CRITICAL, remediation: MANUAL, createdDaysAgo: 2 },
    { id: 'exp-18', companyIdx: 7, status: CLOSED, severity: HIGH, remediation: SST, createdDaysAgo: 75 },
    { id: 'exp-19', companyIdx: 8, status: PENDING, severity: MEDIUM, remediation: GRT, createdDaysAgo: 18 },
    { id: 'exp-20', companyIdx: 8, status: IN_PROGRESS, severity: LOW, remediation: RRT, createdDaysAgo: 33 },
    { id: 'exp-21', companyIdx: 9, status: CLOSED, severity: MEDIUM, remediation: SST, createdDaysAgo: 90 },
    { id: 'exp-22', companyIdx: 9, status: FAILED, severity: HIGH, remediation: MANUAL, createdDaysAgo: 45 },
    { id: 'exp-23', companyIdx: 10, status: EXPOSED, severity: MEDIUM, remediation: MANUAL, createdDaysAgo: 7 },
    { id: 'exp-24', companyIdx: 10, status: PENDING, severity: LOW, remediation: GRT, createdDaysAgo: 20 },
    { id: 'exp-25', companyIdx: 10, status: CLOSED, severity: MEDIUM, remediation: SST, createdDaysAgo: 105 },
    { id: 'exp-26', companyIdx: 11, status: IN_PROGRESS, severity: HIGH, remediation: GRT, createdDaysAgo: 28 },
    { id: 'exp-27', companyIdx: 11, status: CLOSED, severity: LOW, remediation: RRT, createdDaysAgo: 120 },
    { id: 'exp-28', companyIdx: 12, status: PENDING, severity: MEDIUM, remediation: EMAIL, createdDaysAgo: 16 },
    { id: 'exp-29', companyIdx: 12, status: CLOSED, severity: LOW, remediation: SST, createdDaysAgo: 140 },
    { id: 'exp-30', companyIdx: 13, status: EXPOSED, severity: HIGH, remediation: MANUAL, createdDaysAgo: 8 },
    { id: 'exp-31', companyIdx: 13, status: IN_PROGRESS, severity: MEDIUM, remediation: GRT, createdDaysAgo: 35 },
    { id: 'exp-32', companyIdx: 14, status: PENDING, severity: MEDIUM, remediation: EMAIL, createdDaysAgo: 22 },
    { id: 'exp-33', companyIdx: 14, status: CLOSED, severity: LOW, remediation: RRT, createdDaysAgo: 115 },
    { id: 'exp-34', companyIdx: 15, status: FAILED, severity: HIGH, remediation: MANUAL, createdDaysAgo: 50 },
    { id: 'exp-35', companyIdx: 15, status: CLOSED, severity: MEDIUM, remediation: SST, createdDaysAgo: 98 },
    { id: 'exp-36', companyIdx: 16, status: EXPOSED, severity: MEDIUM, remediation: MANUAL, createdDaysAgo: 10 },
    { id: 'exp-37', companyIdx: 16, status: PENDING, severity: HIGH, remediation: GRT, createdDaysAgo: 15 },
    { id: 'exp-38', companyIdx: 16, status: IN_PROGRESS, severity: LOW, remediation: SST, createdDaysAgo: 38 },
    { id: 'exp-39', companyIdx: 17, status: CLOSED, severity: MEDIUM, remediation: RRT, createdDaysAgo: 125 },
    { id: 'exp-40', companyIdx: 17, status: PENDING, severity: LOW, remediation: EMAIL, createdDaysAgo: 19 },
    { id: 'exp-41', companyIdx: 0, status: IN_PROGRESS, severity: HIGH, remediation: SST, createdDaysAgo: 42 },
    { id: 'exp-42', companyIdx: 3, status: PENDING, severity: MEDIUM, remediation: GRT, createdDaysAgo: 13 },
    { id: 'exp-43', companyIdx: 7, status: IN_PROGRESS, severity: CRITICAL, remediation: SST, createdDaysAgo: 27 },
    { id: 'exp-44', companyIdx: 10, status: EXPOSED, severity: HIGH, remediation: MANUAL, createdDaysAgo: 6 },
    { id: 'exp-45', companyIdx: 14, status: IN_PROGRESS, severity: MEDIUM, remediation: GRT, createdDaysAgo: 31 },
];

function timestampsFor(status: ExposureStatus, createdDaysAgo: number) {
    const created_at = demoDaysAgo(createdDaysAgo);
    const inProgressDaysAgo = Math.round(createdDaysAgo * 0.6);
    const closedDaysAgo = Math.round(createdDaysAgo * 0.25);

    let in_progress_at: string | null = null;
    let closed_at: string | null = null;
    let last_action_at = created_at;

    if (status === IN_PROGRESS || status === CLOSED || status === FAILED) {
        in_progress_at = demoDaysAgo(inProgressDaysAgo);
        last_action_at = in_progress_at;
    }
    if (status === CLOSED || status === FAILED) {
        closed_at = demoDaysAgo(closedDaysAgo);
        last_action_at = closed_at;
    }

    return { created_at, in_progress_at, closed_at, last_action_at };
}

function priorityFor(severity: ExposureSeverity): number {
    return { critical: 1, high: 2, medium: 3, low: 4, info: 5, suppressed: 6 }[severity];
}

function nextActionFor(status: ExposureStatus): ExposureNextAction {
    if (status === EXPOSED) return ExposureNextAction.CONFIRM;
    if (status === PENDING) return ExposureNextAction.SUBMIT_PRIVACYBEE;
    return ExposureNextAction.MONITOR;
}

/** Full `Exposure` object — the exposure detail sidebar's shape. */
function buildExposure(seed: ExposureSeed): Exposure {
    const broker = BROKERS[seed.companyIdx];
    const ts = timestampsFor(seed.status, seed.createdDaysAgo);
    return {
        id: seed.id,
        company_id: broker.id,
        company: {
            logo: logoFor(broker),
            name: broker.name,
            type: typeLabelOf(broker),
            slug: broker.slug,
            scanner: 'active',
        },
        person_id: DEMO_PERSON_ID,
        source: broker.domain,
        priority: priorityFor(seed.severity),
        last_scan_at: ts.created_at,
        last_seen_at: ts.last_action_at,
        last_action_at: ts.last_action_at,
        next_action: nextActionFor(seed.status),
        next_action_at: seed.status === CLOSED || seed.status === FAILED ? null : demoDaysAgo(Math.max(seed.createdDaysAgo - 7, 0)),
        status: seed.status,
        created_at: ts.created_at,
        closed_at: ts.closed_at,
        in_progress_at: ts.in_progress_at,
        remediation: seed.remediation,
    };
}

/** The `data.*` bag every company-exposure and recent-exposure fixture shares. */
function exposureDataFor(broker: BrokerSeed, addressIdx: number, categories: DetectedCategory[]) {
    const data: any = {};
    if (categories.includes('name')) {
        data.first_name = DEMO_PERSONA.first_name;
        data.last_name = DEMO_PERSONA.last_name;
        data.name = DEMO_PERSONA.full_name;
    }
    if (categories.includes('date_of_birth')) data.date_of_birth = DEMO_PERSONA.dob;
    if (categories.includes('age')) data.age = String(personaAge());
    if (categories.includes('addresses')) data.addresses = [PERSONA_ADDRESSES[addressIdx % PERSONA_ADDRESSES.length]];
    if (categories.includes('phones')) data.phones = [PERSONA_PHONES[addressIdx % PERSONA_PHONES.length]];
    if (categories.includes('emails')) data.emails = [PERSONA_EMAILS[addressIdx % PERSONA_EMAILS.length]];
    if (categories.includes('relatives')) data.relatives = PERSONA_RELATIVES;
    if (categories.includes('employment')) {
        data.occupation = 'Regional Manager';
        data.location = 'Scranton, PA';
    }
    data.name = data.name ?? DEMO_PERSONA.full_name;
    data.url = profileUrlFor(broker);
    data.screenshot_url = profileScreenshot(broker, addressIdx);
    return data;
}

/** Superset object answering `/people/{id}/companies/{companyId}/exposures` for every caller shape. */
function buildCompanyExposureDetail(seed: ExposureSeed, exposure: Exposure): CompanyExposureDetail & Record<string, any> {
    const broker = BROKERS[seed.companyIdx];
    const categories = detectedCategoriesFor(broker.type);
    return {
        exposure_id: seed.id,
        id: seed.id,
        company_id: broker.id,
        person_id: DEMO_PERSON_ID,
        status: exposure.status,
        profile_url: profileUrlFor(broker),
        created_at: exposure.created_at,
        in_progress_at: exposure.in_progress_at,
        closed_at: exposure.closed_at,
        last_action_at: exposure.last_action_at ?? exposure.created_at,
        last_exposure: exposure.created_at,
        last_action: exposure.last_action_at ?? exposure.created_at,
        next_action: exposure.next_action,
        remediation: exposure.remediation,
        severity: seed.severity,
        events: 1,
        detected_data: categories,
        timeline: {
            exposure_found: exposure.created_at,
            request_sent: exposure.in_progress_at,
            verified_clean: exposure.closed_at,
        },
        data: exposureDataFor(broker, seed.companyIdx, categories),
    };
}

function buildPersonExposure(exposure: Exposure): PersonExposure {
    return {
        id: exposure.id,
        company_id: exposure.company_id,
        person_id: exposure.person_id,
        priority: exposure.priority,
        severity: ExposureSeverity.MEDIUM,
        last_scan_at: exposure.last_scan_at,
        last_seen_at: exposure.last_seen_at,
        status: exposure.status,
        last_action_at: exposure.last_action_at ?? exposure.created_at,
        created_at: exposure.created_at,
    };
}

function activityTypeFor(status: ExposureStatus): 'discovered' | 'in_progress' | 'closed' {
    if (status === CLOSED || status === FAILED) return 'closed';
    if (status === IN_PROGRESS) return 'in_progress';
    return 'discovered';
}

function buildRecentExposure(seed: ExposureSeed, exposure: Exposure, detail: CompanyExposureDetail & Record<string, any>): RecentExposure {
    const broker = BROKERS[seed.companyIdx];
    return {
        exposure_id: seed.id,
        company_id: broker.id,
        status: exposure.status,
        activity_type: activityTypeFor(exposure.status),
        activity_date: exposure.last_action_at ?? exposure.created_at,
        created_at: exposure.created_at,
        in_progress_at: exposure.in_progress_at,
        closed_at: exposure.closed_at,
        profile_url: detail.profile_url,
        data: {
            first_name: DEMO_PERSONA.first_name,
            last_name: DEMO_PERSONA.last_name,
            name: DEMO_PERSONA.full_name,
            emails: detail.data?.emails ?? [DEMO_PERSONA.email],
            phones: detail.data?.phones ?? [DEMO_PERSONA.phone],
            addresses: detail.data?.addresses ?? [PERSONA_ADDRESSES[0]],
            aka: PERSONA_AKA,
            relatives: detail.data?.relatives ?? [],
            age: String(personaAge()),
            date_of_birth: DEMO_PERSONA.dob,
            screenshot_full_path: detail.data?.screenshot_url,
            url: detail.data?.url,
            search_url: detail.data?.url,
            record_id: seed.id,
        },
        company: companyMini(broker),
    };
}

// ---------------------------------------------------------------------------
// Derived collections
// ---------------------------------------------------------------------------

const EXPOSURES: Exposure[] = EXPOSURE_SEEDS.map(buildExposure);
const COMPANY_EXPOSURE_DETAILS: (CompanyExposureDetail & Record<string, any>)[] = EXPOSURE_SEEDS.map((seed, i) =>
    buildCompanyExposureDetail(seed, EXPOSURES[i])
);
const PERSON_EXPOSURES: PersonExposure[] = EXPOSURES.map(buildPersonExposure);
const RECENT_EXPOSURES: RecentExposure[] = EXPOSURE_SEEDS.map((seed, i) => buildRecentExposure(seed, EXPOSURES[i], COMPANY_EXPOSURE_DETAILS[i]))
    .sort((a, b) => new Date(b.activity_date).getTime() - new Date(a.activity_date).getTime());

const OPEN_STATUSES = new Set([EXPOSED, PENDING]);
const CLOSED_STATUSES = new Set([CLOSED, FAILED]);

function coverageFor(idx: number): CoverageStatus {
    if (idx < 12) return 'active';
    if (idx < 15) return 'disabled';
    return 'no_access';
}

// Privacy Bee monitors the whole catalog for every member, but a real person turns up on a
// small slice of it — a couple of hundred sites, not a thousand. So most companies here are
// monitored and clean, and only the slice below carries exposure history. Derived from the
// row index rather than drawn at random, so every reload of the demo shows the same figures.
function backgroundAggregate(broker: BrokerSeed, idx: number): CompanyExposure {
    const base = {
        id: broker.id,
        name: broker.name,
        slug: broker.slug,
        logo: logoFor(broker),
        url: urlFor(broker.domain),
        type: typeLabelOf(broker),
        coverage: (idx % 37 === 0 ? 'no_access' : idx % 11 === 0 ? 'disabled' : 'active') as CoverageStatus,
    };

    if (idx % 183 === 0) {
        return { ...base, count: 2, events: 2, status: FAILED, active: 0, in_progress: 0, closed: 1, completed: 0, last_action: demoDaysAgo(30 + (broker.id % 120)) };
    }
    if (idx % 79 === 0) {
        return { ...base, count: 6, events: 6, status: EXPOSED, active: 6, in_progress: 0, closed: 0, completed: 0, last_action: demoDaysAgo(broker.id % 9) };
    }
    if (idx % 29 === 0) {
        return { ...base, count: 8, events: 8, status: IN_PROGRESS, active: 8, in_progress: 8, closed: 0, completed: 0, last_action: demoDaysAgo(2 + (broker.id % 20)) };
    }
    if (idx % 8 === 0) {
        return { ...base, count: 2, events: 2, status: CLOSED, active: 0, in_progress: 0, closed: 1, completed: 1, last_action: demoDaysAgo(12 + (broker.id % 260)) };
    }

    // Monitored, scanned, nothing found — the table renders this as "Clean".
    return { ...base, count: 0, events: 0, status: NOT_FOUND, active: 0, in_progress: 0, closed: 0, completed: 0, last_action: demoDaysAgo(1 + (idx % 21)) };
}

/** One row per monitored company — the `/exposures/detail` aggregation the tables render. */
const COMPANY_AGGREGATES: CompanyExposure[] = MONITORED.map((broker, idx) => {
    if (!CURATED_IDS.has(broker.id)) {
        return backgroundAggregate(broker, idx);
    }

    const rows = EXPOSURES.filter((e) => e.company_id === broker.id);
    const active = rows.filter((e) => OPEN_STATUSES.has(e.status)).length;
    const in_progress = rows.filter((e) => e.status === IN_PROGRESS).length;
    const closed = rows.filter((e) => CLOSED_STATUSES.has(e.status)).length;
    const completed = rows.filter((e) => e.status === CLOSED).length;
    const lastAction = rows.reduce<string>((latest, e) => {
        const at = e.last_action_at ?? e.created_at;
        return !latest || new Date(at).getTime() > new Date(latest).getTime() ? at : latest;
    }, rows[0]?.last_action_at ?? rows[0]?.created_at ?? demoDaysAgo(30));
    const status = rows.some((e) => e.status === EXPOSED)
        ? EXPOSED
        : rows.some((e) => e.status === PENDING)
          ? PENDING
          : rows.some((e) => e.status === IN_PROGRESS)
            ? IN_PROGRESS
            : rows.some((e) => e.status === FAILED)
              ? FAILED
              : CLOSED;

    return {
        id: broker.id,
        name: broker.name,
        slug: broker.slug,
        count: rows.length,
        logo: logoFor(broker),
        url: urlFor(broker.domain),
        events: rows.length,
        type: typeLabelOf(broker),
        last_action: lastAction,
        status,
        active,
        in_progress,
        closed,
        completed,
        coverage: coverageFor(idx),
    };
});

// ---------------------------------------------------------------------------
// Conversation threads — a couple of exposures get real back-and-forth history
// ---------------------------------------------------------------------------

function message(
    exposureId: string,
    type: ConversationMessageType,
    text: string,
    author: string,
    daysAgo: number,
    scan: ScanRecord,
    seq: number
): ConversationMessage {
    const ts = demoDaysAgo(daysAgo);
    return {
        author,
        conversation_id: `conv-${exposureId}`,
        expiration: Date.now() + 365 * 24 * 60 * 60 * 1000,
        exposure_id: exposureId,
        message: { content: { message: text }, type },
        message_id: `${exposureId}-msg-${seq}`,
        scan,
        timestamp_ms: new Date(ts).getTime(),
    };
}

function threadFor(seedId: string): ConversationMessage[] {
    const seed = EXPOSURE_SEEDS.find((s) => s.id === seedId)!;
    const broker = BROKERS[seed.companyIdx];
    const scan = scanRecordFor(broker, seed.companyIdx);
    const T = ConversationMessageType;

    if (seedId === 'exp-6') {
        // Closed lifecycle: found -> request created -> submitted -> confirmed removed
        return [
            message(seedId, T.SCAN_HIT, `We found Michael's information exposed on ${broker.name}.`, 'system', 110, scan, 1),
            message(seedId, T.PRIVACY_REQUEST_CREATED, `A removal request was created for ${broker.name}.`, 'privacybee', 108, scan, 2),
            message(seedId, T.PRIVACY_REQUEST_SUBMITTED, `Removal request submitted to ${broker.name}.`, 'privacybee', 104, scan, 3),
            message(seedId, T.PRIVACY_REQUEST_CLOSED, `${broker.name} confirmed the record was removed.`, 'privacybee', 27, scan, 4),
        ];
    }
    if (seedId === 'exp-16') {
        // Still in progress: found -> request created -> submitted -> awaiting confirmation
        return [
            message(seedId, T.SCAN_HIT, `We found Michael's information exposed on ${broker.name}.`, 'system', 40, scan, 1),
            message(seedId, T.PRIVACY_REQUEST_CREATED, `A removal request was created for ${broker.name}.`, 'privacybee', 39, scan, 2),
            message(seedId, T.PRIVACY_REQUEST_SUBMITTED, `Removal request submitted to ${broker.name}.`, 'privacybee', 36, scan, 3),
            message(seedId, T.NOTIFY_DATA_EXPOSED_ON_COMPANY, `Still visible on ${broker.name}; we're following up.`, 'privacybee', 16, scan, 4),
        ];
    }
    if (seedId === 'exp-22') {
        // Failed: found -> request created -> submitted -> rejected
        return [
            message(seedId, T.SCAN_HIT, `We found Michael's information exposed on ${broker.name}.`, 'system', 45, scan, 1),
            message(seedId, T.PRIVACY_REQUEST_CREATED, `A removal request was created for ${broker.name}.`, 'privacybee', 44, scan, 2),
            message(seedId, T.PRIVACY_REQUEST_SUBMITTED, `Removal request submitted to ${broker.name}.`, 'privacybee', 41, scan, 3),
            message(seedId, T.COMPANY_EXPOSURE_REQUEST_REJECTED, `${broker.name} rejected the request; escalating for manual review.`, 'privacybee', 38, scan, 4),
            message(seedId, T.PRIVACY_REQUEST_REOPENED, `Request reopened and reassigned to our Global Response Team.`, 'privacybee', 35, scan, 5),
        ];
    }
    return [];
}

const CURATED_THREADS = ['exp-6', 'exp-16', 'exp-22'];
const EXPOSURE_HISTORY = new Map<string, ConversationMessage[]>(
    CURATED_THREADS.map((id): [string, ConversationMessage[]] => [id, threadFor(id)])
);

function historyForExposure(exposureId: string): ConversationMessage[] {
    return EXPOSURE_HISTORY.get(exposureId) ?? [];
}

function historyForCompany(companyId: number): ConversationMessage[] {
    const idsForCompany = EXPOSURE_SEEDS.filter((s) => BROKERS[s.companyIdx].id === companyId).map((s) => s.id);
    return CURATED_THREADS.filter((id) => idsForCompany.includes(id)).flatMap((id) => historyForExposure(id));
}

// ---------------------------------------------------------------------------
// Potential exposures (data-exposed/v2) + suppressions
// ---------------------------------------------------------------------------

const SOURCE_COMPANIES = BROKERS.slice(0, 8).map((b, i) => vaultCompanyRef(b, 5 + i * 3));

const POTENTIAL_EXPOSURES: PotentialExposuresResponseData = {
    name: [
        { value: DEMO_PERSONA.full_name, companies: SOURCE_COMPANIES, confirmed: false, suppressed: false },
        { value: 'Michael G Scott', companies: SOURCE_COMPANIES.slice(0, 4), confirmed: false, suppressed: false },
    ],
    age: [{ value: String(personaAge()), companies: SOURCE_COMPANIES.slice(0, 5), confirmed: false, suppressed: false }],
    date_of_birth: [{ value: DEMO_PERSONA.dob, companies: SOURCE_COMPANIES.slice(0, 3), confirmed: false, suppressed: false }],
    addresses: [
        { value: { ...PERSONA_ADDRESSES[0], primary: true }, companies: SOURCE_COMPANIES.slice(0, 6), confirmed: true, suppressed: false },
        { value: { ...PERSONA_ADDRESSES[1], primary: false }, companies: SOURCE_COMPANIES.slice(2, 5), confirmed: false, suppressed: false },
        { value: { ...PERSONA_ADDRESSES[2], primary: false }, companies: SOURCE_COMPANIES.slice(4, 7), confirmed: false, suppressed: false },
    ],
    relatives: PERSONA_RELATIVES.map((r) => ({
        original: { ...r, middle_name: null },
        value: { ...r, middle_name: null },
        companies: SOURCE_COMPANIES.slice(0, 4),
        confirmed: false,
        suppressed: false,
    })),
    // Everything below is deliberately absent from the vault: the dashboard's "potential
    // exposures" banner only counts values it cannot already match to a vault record, so
    // anything Michael has confirmed would never reach the review queue.
    aka: [
        { value: 'Mike Scott', companies: SOURCE_COMPANIES.slice(0, 3), confirmed: false, suppressed: false },
        { value: 'Michael Scarn', companies: SOURCE_COMPANIES.slice(1, 5), confirmed: false, suppressed: false },
        { value: 'M Scott', companies: SOURCE_COMPANIES.slice(2, 6), confirmed: false, suppressed: false },
        { value: 'Michael Scott Jr', companies: SOURCE_COMPANIES.slice(0, 2), confirmed: false, suppressed: false },
        { value: 'Mickey Scott', companies: SOURCE_COMPANIES.slice(4, 8), confirmed: false, suppressed: false },
    ],
    emails: [
        { value: PERSONA_EMAILS[1], companies: SOURCE_COMPANIES.slice(0, 5), confirmed: false, suppressed: false },
        { value: PERSONA_EMAILS[2], companies: SOURCE_COMPANIES.slice(3, 6), confirmed: false, suppressed: false },
        { value: 'michaelscarn@gmail.com', companies: SOURCE_COMPANIES.slice(0, 4), confirmed: false, suppressed: false },
        { value: 'worldsbestboss@yahoo.com', companies: SOURCE_COMPANIES.slice(2, 7), confirmed: false, suppressed: false },
        { value: 'm.scott@dundermifflin.com', companies: SOURCE_COMPANIES.slice(1, 3), confirmed: false, suppressed: false },
        { value: 'mikescott.scranton@aol.com', companies: SOURCE_COMPANIES.slice(5, 8), confirmed: false, suppressed: false },
    ],
    phones: [
        { value: PERSONA_PHONES[1], companies: SOURCE_COMPANIES.slice(1, 4), confirmed: false, suppressed: true },
        { value: '+15705557412', companies: SOURCE_COMPANIES.slice(0, 5), confirmed: false, suppressed: false },
        { value: '+15705553388', companies: SOURCE_COMPANIES.slice(3, 7), confirmed: false, suppressed: false },
        { value: '+15705551104', companies: SOURCE_COMPANIES.slice(2, 4), confirmed: false, suppressed: false },
        { value: '+12125550187', companies: SOURCE_COMPANIES.slice(6, 8), confirmed: false, suppressed: false },
    ],
};

// In-memory so the ignore/confirm/delete flow round-trips within a demo session.
let SUPPRESSIONS: SuppressionRecord[] = [
    {
        id: 9001,
        person_id: DEMO_PERSON_ID,
        me: 1,
        type: 'address',
        data: {
            line1: PERSONA_ADDRESSES[0].line1,
            line2: PERSONA_ADDRESSES[0].line2,
            city: PERSONA_ADDRESSES[0].city,
            state: PERSONA_ADDRESSES[0].state,
            postal_code: PERSONA_ADDRESSES[0].postal_code,
            country: PERSONA_ADDRESSES[0].country,
        },
        created_at: demoDaysAgo(60),
        updated_at: demoDaysAgo(60),
    },
    {
        id: 9002,
        person_id: DEMO_PERSON_ID,
        me: 0,
        type: 'phone',
        data: PERSONA_PHONES[1],
        created_at: demoDaysAgo(20),
        updated_at: demoDaysAgo(20),
    },
];
let nextSuppressionId = 9003;

// ---------------------------------------------------------------------------
// Digital footprint and removal figures
// ---------------------------------------------------------------------------

// Scanning is the one number that is genuinely large: the whole catalog is re-swept every
// month, so a member has thousands of scans behind a couple of hundred exposures. Everything
// else is a share of the account's all-time totals, so a shorter window is always a subset of
// the lifetime figures the removal-activity chart shows.
const SCANS_PER_MONTH = 830;

const sumAggregates = (pick: (c: CompanyExposure) => number) => COMPANY_AGGREGATES.reduce((n, c) => n + pick(c), 0);

const TOTAL_EVENTS = sumAggregates((c) => c.events);
const TOTAL_COMPLETED = sumAggregates((c) => c.completed);
const TOTAL_IN_PROGRESS = sumAggregates((c) => c.in_progress);

// The funnel behind the removal work: a broker listing turns up many separate records, a
// subset become removal requests, and a subset of those are confirmed gone. Every figure is
// summed from the same per-company monitoring record the removal chart and the Data Brokers
// page read, so the three of them can never drift apart.
const REMOVALS_COMPLETED_ALL_TIME = TOTAL_COMPLETED;
const REMOVALS_REQUESTED_ALL_TIME = TOTAL_COMPLETED + TOTAL_IN_PROGRESS;
const EXPOSURES_FOUND_ALL_TIME = TOTAL_EVENTS;

// The detail answer's `meta.removal_activity`, derived once from the detail's own rows by the
// server's definition: closed rows are completed, in-progress rows are in progress, requested is
// the two together, and every row at a company beyond its first is a re-exposure.
const REMOVAL_ACTIVITY: RemovalActivity = {
    requested: REMOVALS_REQUESTED_ALL_TIME,
    completed: REMOVALS_COMPLETED_ALL_TIME,
    in_progress: TOTAL_IN_PROGRESS,
    re_exposures: sumAggregates((c) => Math.max(0, c.count - 1)),
};

function digitalFootprintStats(months: ExposureStatsWindow): DigitalFootprintFigures {
    const share = Math.min(1, months / 6);
    return {
        scans_performed: Math.round(months * SCANS_PER_MONTH),
        exposures_found: Math.round(EXPOSURES_FOUND_ALL_TIME * share),
        removals_requested: Math.round(REMOVALS_REQUESTED_ALL_TIME * share),
        removals_completed: Math.round(REMOVALS_COMPLETED_ALL_TIME * share),
    };
}

// The member's risk as the stats read states it: the score now, and the previous score, the latest recorded on or
// before 30 days back. The demo member's score has fallen four points since then.
const FORECAST: ExposureStats['forecast'] = { risk: DEMO_PERSONA.risk_score, previous: DEMO_PERSONA.risk_score + 4 };

// The removal figures standing now are the detail's own tally, as the API states them in both reads.
const EXPOSURE_STATS_ALL_TIME: ExposureStats['all_time'] = { removals_completed: REMOVAL_ACTIVITY.completed };
const EXPOSURE_STATS_CURRENT: ExposureStats['current'] = { removals_in_progress: REMOVAL_ACTIVITY.in_progress };

const STATS_WINDOW_REFUSAL = `The months window is not one this endpoint offers: ${EXPOSURE_STATS_WINDOWS.slice(0, -1).join(', ')} or ${EXPOSURE_STATS_WINDOWS.at(-1)}.`;

/**
 * The window a stats request's `months` names, read as app-api reads the query: absent, empty or blank is the default
 * window; anything but exactly the digits of an offered window names none (null), and so does a `months[]` list. PHP
 * keeps the last of a repeated parameter, and a bracketed key makes it a list.
 */
function statsWindow(query: URLSearchParams): ExposureStatsWindow | null {
    let months: string | string[] | undefined;
    for (const [key, value] of query) {
        if (key === 'months') months = value;
        else if (key.startsWith('months[')) months = [value];
    }

    if (months === undefined || (typeof months === 'string' && months.trim() === '')) return DEFAULT_EXPOSURE_STATS_WINDOW;
    if (typeof months !== 'string') return null;
    return EXPOSURE_STATS_WINDOWS.find((window) => String(window) === months) ?? null;
}

// ---------------------------------------------------------------------------
// Company detail / search / advisories / guide
// ---------------------------------------------------------------------------

function companyDetail(companyId: number): CompanyDetail {
    const broker = findBroker(companyId);
    return {
        id: broker.id,
        name: broker.name,
        url: urlFor(broker.domain),
        logo: logoFor(broker),
        slug: broker.slug,
        trust_score: broker.type === 'people_search' || broker.type === 'people_search_affiliate' ? 2.4 : 3.6,
        phone_numbers: [],
        addresses: [],
        type: typeLabelOf(broker),
        description: `${broker.name} aggregates public records and online activity into searchable people-search profiles.`,
        score: 62,
        parent: { id: broker.id, name: broker.name, slug: broker.slug },
        privacy_policy: `${urlFor(broker.domain)}/privacy`,
        scanner: ScannerStatus.ACTIVE,
        deletion_required_fields: broker.type === 'people_search' ? ['full_name', 'address'] : null,
    };
}

function searchCatalog(query: URLSearchParams, catalog: BrokerSeed[]) {
    const search = (query.get('search') ?? '').trim().toLowerCase();
    const type = (query.get('type') ?? '').trim();
    const perPage = Number(query.get('per_page')) || 20;
    const page = Number(query.get('page')) || 1;

    const filtered = catalog.filter((c) => {
        const matchesSearch = !search || c.name.toLowerCase().includes(search);
        const matchesType = !type || c.type === type;
        return matchesSearch && matchesType;
    });

    const total = filtered.length;
    const lastPage = Math.max(Math.ceil(total / perPage), 1);
    const start = (page - 1) * perPage;
    const pageItems = filtered.slice(start, start + perPage).map((broker) => ({ ...companyDetail(broker.id), status: 'active' }));

    const meta: Meta = { total, per_page: perPage, current_page: page, from: total ? start + 1 : 0, to: Math.min(start + perPage, total), last_page: lastPage };
    return envelope(pageItems, meta);
}

const ADVISORY_COMPANY_IDS = new Set([BROKERS[2].id, BROKERS[5].id]); // BeenVerified, MyLife

function advisoriesFor(companyId: number): CompanyAdvisory[] {
    if (!ADVISORY_COMPANY_IDS.has(companyId)) return [];
    const broker = findBroker(companyId);
    return [
        {
            id: `advisory-${companyId}`,
            title: `${broker.name} has faced regulatory scrutiny over data resale practices`,
            html: `<p>${broker.name} has been named in state attorney general inquiries into how people-search data is sourced and resold. Removal requests can take longer to process during active review periods.</p>`,
            url: `${urlFor(broker.domain)}/legal`,
            type: 'regulatory',
            created_at: demoDaysAgo(200),
            updated_at: demoDaysAgo(45),
        },
    ];
}

function deletionGuideFor(companyId: number): DeletionGuideResponse {
    const broker = findBroker(companyId);
    return {
        guide: `<ol><li>Visit the ${broker.name} opt-out page at ${urlFor(broker.domain)}/optout.</li><li>Search for "${DEMO_PERSONA.full_name}" and locate the matching profile.</li><li>Submit the removal request and confirm via the verification email.</li><li>Allow up to 14 days for ${broker.name} to process the removal.</li></ol>`,
    };
}

// ---------------------------------------------------------------------------
// Routes
// ---------------------------------------------------------------------------

export const exposureRoutes: DemoRoute[] = [
    // --- literal exposure sub-resources (must precede the generic /exposures/{id} route) ---
    {
        pattern: /^people\/\d+\/exposures\/([^/]+)\/history$/,
        respond: (req) => envelope(historyForExposure(decodeURIComponent(req.match[1]))),
    },
    {
        pattern: /^people\/\d+\/exposures\/company\/(\d+)\/history$/,
        respond: (req) => envelope(historyForCompany(Number(req.match[1]))),
    },
    {
        pattern: /^people\/\d+\/exposures\/stats$/,
        status: (req) => (statsWindow(req.query) === null ? 422 : 200),
        respond: (req) => {
            const months = statsWindow(req.query);
            if (months === null) return refusal(req.path, 422, STATS_WINDOW_REFUSAL);

            return envelope<ExposureStats>({
                forecast: FORECAST,
                all_time: EXPOSURE_STATS_ALL_TIME,
                current: EXPOSURE_STATS_CURRENT,
                digital_footprint: digitalFootprintStats(months),
            });
        },
    },
    {
        pattern: /^people\/\d+\/exposures\/data-exposed\/v2$/,
        respond: () => envelope(POTENTIAL_EXPOSURES),
    },
    {
        pattern: /^people\/\d+\/exposures\/detail$/,
        respond: () => envelope(COMPANY_AGGREGATES, { removal_activity: REMOVAL_ACTIVITY }),
    },
    {
        pattern: /^people\/\d+\/exposures\/recent$/,
        respond: (req) => {
            const limit = Number(req.query.get('limit')) || 6;
            return envelope(RECENT_EXPOSURES.slice(0, limit));
        },
    },
    {
        pattern: /^people\/\d+\/exposures\/suppressions$/,
        respond: () => envelope(SUPPRESSIONS),
    },
    {
        pattern: /^people\/\d+\/exposures\/suppressions$/,
        method: 'PUT',
        status: 201,
        respond: (req): BaseAPIResponse<SuppressionRecord> => {
            const record: SuppressionRecord = {
                id: nextSuppressionId++,
                person_id: DEMO_PERSON_ID,
                me: req.body?.me ? 1 : 0,
                type: req.body?.type,
                data: req.body?.data,
                created_at: demoDaysAgo(0),
                updated_at: demoDaysAgo(0),
            };
            SUPPRESSIONS = [...SUPPRESSIONS, record];
            return envelope(record);
        },
    },
    {
        pattern: /^people\/\d+\/exposures\/suppressions\/(\d+)$/,
        method: 'DELETE',
        respond: (req) => {
            const id = Number(req.match[1]);
            SUPPRESSIONS = SUPPRESSIONS.filter((r) => r.id !== id);
            return envelope(null);
        },
    },
    {
        pattern: /^people\/\d+\/exposures\/custom-removal$/,
        method: 'POST',
        respond: (req) => {
            const url = String(req.body?.url ?? '');
            let host = 'example.com';
            try {
                host = new URL(url.startsWith('http') ? url : `https://${url}`).hostname.replace(/^www\./, '');
            } catch {
                // malformed URL from the form — fall through with the example.com placeholder
            }
            const known = ALL_COMPANIES.find((c) => host.includes(c.domain) || c.domain.includes(host));
            const exposureId = `exp-custom-${Date.now()}`;
            return {
                success: true,
                message: known
                    ? `We found an existing listing on ${known.name} and started a removal request.`
                    : `We've queued a custom removal request for ${host}.`,
                exposure_id: exposureId,
                company: known ? { id: known.id, name: known.name, type: known.type } : { id: 7000, name: host, type: null },
            };
        },
    },
    {
        pattern: /^people\/\d+\/exposures$/,
        respond: (req) => {
            const limit = Number(req.query.get('limit')) || 50;
            return envelope(PERSON_EXPOSURES.slice(0, limit));
        },
    },
    // --- generic single exposure — must stay last among /exposures/* patterns ---
    {
        pattern: /^people\/\d+\/exposures\/([^/]+)$/,
        respond: (req) => {
            const exposure = EXPOSURES.find((e) => e.id === req.match[1]) ?? EXPOSURES[0];
            return envelope(exposure);
        },
    },

    // --- person <-> company actions ---
    {
        pattern: /^people\/\d+\/companies\/(\d+)\/untrust$/,
        method: 'POST',
        status: 201,
        respond: () => envelope(null),
    },
    {
        pattern: /^people\/\d+\/companies\/(\d+)\/exposures$/,
        respond: (req) => {
            const companyId = Number(req.match[1]);
            return envelope(COMPANY_EXPOSURE_DETAILS.filter((d) => d.company_id === companyId));
        },
    },

    // --- company directory ---
    {
        pattern: /^companies\/request$/,
        method: 'POST',
        status: 201,
        respond: () => envelope(null),
    },
    {
        pattern: /^companies\/(\d+)$/,
        respond: (req) => envelope(companyDetail(Number(req.match[1]))),
    },
    {
        pattern: /^companies$/,
        respond: (req) => searchCatalog(req.query, ALL_COMPANIES),
    },
    {
        pattern: /^public\/companies\/(\d+)\/advisories$/,
        respond: (req) => envelope(advisoriesFor(Number(req.match[1]))),
    },
    {
        pattern: /^public\/companies\/(\d+)\/guide$/,
        respond: (req) => envelope(deletionGuideFor(Number(req.match[1]))),
    },
    {
        pattern: /^public\/companies$/,
        respond: (req) => searchCatalog(req.query, ALL_COMPANIES),
    },
];
